import React, { forwardRef, ReactNode } from "react";
import { IconChevronDown, IconChevronUp } from "../../icons";
import { useControllableState } from "../../../helpers/state/state-hooks";
import styles from "./button-expandable.module.scss";
import classNames from "classnames";
import { Button, ButtonProps } from "../button";

export interface ButtonExpandableProps extends ButtonProps {
  /** Is the button currently expanded? */
  expanded?: boolean;
  /** Callback that is invoked when the button is expanded or collapsed. */
  onExpandChange?: (expanded: boolean) => void;
  /** Icon to set for chevron when button is expanded. Defaults to IconChevronDown. */
  expandedChevron?: ReactNode;
  /** Icon to set for chevron when button is collapsed. Defaults to IconChevronUp. */
  collapsedChevron?: ReactNode;
}

export const ButtonExpandable = forwardRef<
  HTMLButtonElement,
  ButtonExpandableProps
>((props, ref) => {
  const {
    expanded,
    onExpandChange,
    expandedChevron = <IconChevronUp />,
    collapsedChevron = <IconChevronDown />,
    className,
    children,
    ...rest
  } = props;

  const [isExpanded, setExpanded] = useControllableState({
    prop: expanded,
    defaultProp: false,
    onChange: onExpandChange,
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setExpanded((prev) => !prev);
    if (props.onClick) {
      props.onClick(e);
    }
  };

  const classes = classNames(styles.content, styles.button, className);

  return (
    <Button
      {...rest}
      ref={ref}
      onClick={handleClick}
      className={classes}
      data-expanded={expanded}
    >
      {children}
      <div className={styles.chevron}>
        {isExpanded ? expandedChevron : collapsedChevron}
      </div>
    </Button>
  );
});

ButtonExpandable.displayName = "ExpandableButton";
